import { useMemo, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SubtitlesIcon from '@mui/icons-material/Subtitles';

/**
 * Scannable transcript lane beneath the cognitive trace — the "what was said"
 * track that replaced the per-marker audio megaphones (consolidation step 4).
 *
 * Each row is one AI-labelled moment (`headline` + the Whisper `transcript`),
 * keyed to a session-elapsed timestamp on the same axis as the chart. The link
 * runs both ways:
 *   - click a row → that instant is pinned: the chart draws its single marker
 *     there and the MediaStrip seeks the video / plays the ±5 s audio window;
 *   - click anywhere on the chart → the nearest row scrolls into view, so a
 *     click on an interesting dip lands you on what was being said around it.
 *
 * "Nearest" is unconditional for the scroll — on a long recording the closest
 * line can be minutes away and you still want to be taken there — but the
 * highlight is only the strong "this is the moment" treatment within
 * NEAR_WINDOW_S. Past that the row is marked as merely the closest, so a click
 * in a silent stretch never implies those words were spoken there.
 *
 * Reusable across the dashboard (single recording) and the Analysis overlay
 * (the focused participant's transcript).
 */

export interface CaptionEntry {
  /** Session-elapsed seconds — matches the chart's x-axis + the pin value. */
  t: number;
  headline: string;
  /** Raw speech for this moment; shown as the row body (falls back to the
   *  one-sentence summary when the transcript is empty). */
  transcript: string;
  summary: string;
}

interface Props {
  entries: CaptionEntry[];
  /** The pinned session-elapsed second (from a chart click), or null. */
  pinnedT: number | null;
  onPick: (t: number) => void;
  /**
   * How to stamp a row, when the host's axis isn't session-elapsed. The
   * Analysis overlay runs on a shared wall clock across participants, so its
   * rows read 11:02:14 while the Dashboard's read 5:42 — a caption showing
   * different units from the chart it is wired to is a small lie about what
   * the click will do. Defaults to elapsed m:ss.
   */
  formatStamp?: (t: number) => string;
  /**
   * How tall the scrolling list may get, when it stands on its own (under the
   * chart, say).
   */
  maxHeight?: number;
  /**
   * Fill the height its container gives it instead of capping at `maxHeight`.
   * Set when the track sits BESIDE the video: the video's height is set by the
   * clip and the window, so a fixed cap either leaves a gap under the
   * transcript or makes it the taller of the two. Filling keeps the block a
   * clean rectangle at any window size.
   */
  fill?: boolean;
}

/** How close a pinned moment has to be for a caption to count as *the*
 *  moment rather than merely the nearest one. A Whisper segment is a few
 *  seconds long, so 6 s is about one line either side. */
const NEAR_WINDOW_S = 6;

/**
 * Two rows saying the same words this close together are the same moment
 * transcribed twice, not a repetition worth reading. It happens when
 * consecutive markers land near each other: their audio windows overlap, so
 * Whisper returns the same text and the paraphrase step gives each a different
 * headline ("Camera check" / "Camera setup" over one sentence). Wide enough to
 * catch that, far short of a speaker genuinely repeating themselves later.
 */
const DUP_WINDOW_S = 20;

/** Normalised body text, for the duplicate check. */
function bodyKey(e: CaptionEntry): string {
  return (e.transcript.trim() || e.summary.trim())
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function formatElapsed(t: number): string {
  const s = Math.max(0, Math.round(t));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

export function CaptionTrack({
  entries,
  pinnedT,
  onPick,
  formatStamp = formatElapsed,
  maxHeight = 168,
  fill = false,
}: Props) {
  const sorted = useMemo(() => {
    const rows = [...entries].sort((a, b) => a.t - b.t);
    const out: CaptionEntry[] = [];
    for (const e of rows) {
      const prev = out[out.length - 1];
      const key = bodyKey(e);
      if (
        prev &&
        key !== '' &&
        bodyKey(prev) === key &&
        e.t - prev.t <= DUP_WINDOW_S
      ) {
        // Keep the earlier stamp, but take a headline from the duplicate when
        // the row we kept has none — the segment lines carry the words, the
        // labelled line carries the "what was happening".
        if (!prev.headline.trim() && e.headline.trim()) {
          prev.headline = e.headline;
          if (!prev.summary.trim()) prev.summary = e.summary;
        }
        continue;
      }
      out.push({ ...e });
    }
    return out;
  }, [entries]);

  // The row closest to the pinned moment, however far that is.
  const nearest = useMemo(() => {
    if (pinnedT == null || sorted.length === 0) {
      return { idx: -1, distance: Infinity };
    }
    let idx = 0;
    let distance = Infinity;
    sorted.forEach((e, i) => {
      const d = Math.abs(e.t - pinnedT);
      if (d < distance) {
        distance = d;
        idx = i;
      }
    });
    return { idx, distance };
  }, [sorted, pinnedT]);

  const activeIdx = nearest.distance <= NEAR_WINDOW_S ? nearest.idx : -1;

  // Scroll to the closest row on every pin, not just the close ones — a click
  // on the chart is a request to read what was said around there.
  const nearestRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    // Optional call: scrollIntoView is missing in some environments (jsdom,
    // older embedded webviews). Failing to scroll is a nuisance; throwing
    // here would take the whole transcript panel down with it.
    nearestRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [nearest.idx]);

  if (sorted.length === 0) return null;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        bgcolor: 'background.default',
        overflow: 'hidden',
        ...(fill && {
          // Take the height the aside column has (which is the video's), not
          // the height the captions want, and scroll inside it.
          flex: 1,
          minHeight: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}
      >
        <SubtitlesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: '.06em', color: 'text.secondary' }}
        >
          TRANSCRIPT
        </Typography>
        <Typography variant="caption" color="text.secondary">
          · click a line to mark it on the trace and play it there; click it
          again to stop
        </Typography>
      </Stack>
      {/* Scrolls on BOTH axes: down through the moments, and sideways through
          a line too long for the column. The box keeps its height either way —
          a transcript must not be what decides how tall this block is. Rows
          are single-line (see below), so the horizontal bar is what gets you
          the rest of a sentence. */}
      <Box
        sx={{
          overflowY: 'auto',
          overflowX: 'auto',
          ...(fill
            ? { flex: 1, minHeight: 0 }
            : { maxHeight }),
        }}
      >
        {sorted.map((e, i) => {
          const active = i === activeIdx;
          // Closest to the click but outside the window: worth showing where
          // you landed, not worth claiming it as the moment.
          const nearby = !active && i === nearest.idx;
          const body = e.transcript.trim() || e.summary.trim();
          return (
            <Box
              key={`${e.t.toFixed(2)}-${i}`}
              component="button"
              type="button"
              ref={i === nearest.idx ? nearestRef : undefined}
              onClick={() => onPick(e.t)}
              aria-label={`jump to ${formatStamp(e.t)}`}
              // 'active' = the pinned moment, 'nearest' = merely the closest
              // line to a click that landed in a quiet stretch. Exposed as an
              // attribute so the distinction is assertable without pinning
              // tests to theme colours.
              data-state={active ? 'active' : nearby ? 'nearest' : 'idle'}
              sx={{
                display: 'flex',
                gap: 1,
                // `max-content` so a long line extends past the column instead
                // of wrapping — that overflow is what the horizontal bar
                // scrolls. `min-width: 100%` keeps short rows full-width so
                // the hover/active band still spans the box.
                width: 'max-content',
                minWidth: '100%',
                textAlign: 'left',
                alignItems: 'baseline',
                px: 1,
                py: 0.6,
                border: 0,
                borderLeft: 3,
                borderLeftColor: active
                  ? 'primary.main'
                  : nearby
                    ? 'primary.light'
                    : 'transparent',
                bgcolor: active
                  ? 'action.selected'
                  : nearby
                    ? 'action.hover'
                    : 'transparent',
                cursor: 'pointer',
                font: 'inherit',
                color: 'text.primary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Chip
                label={formatStamp(e.t)}
                size="small"
                variant="outlined"
                sx={{
                  height: 18,
                  fontSize: 11,
                  fontVariantNumeric: 'tabular-nums',
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                {e.headline.trim() && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      display: 'block',
                      lineHeight: 1.3,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {e.headline}
                  </Typography>
                )}
                {body && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      lineHeight: 1.35,
                      // One line per moment: the list stays scannable and each
                      // row keeps the same height, instead of a talky stretch
                      // pushing the rest out of view. The full sentence is
                      // there — scroll right, or click the line, which pins it
                      // and plays it.
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {body}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
